/** @jsxImportSource @emotion/react */
import MuiAutocomplete from '@mui/material/Autocomplete';
import Paper from '@mui/material/Paper';
import { Selectable, TextFields } from '@zip/business-components';
import * as Icons from '@zip/icons';
import { ComponentProps, FC, FocusEvent, SyntheticEvent } from 'react';

import * as styles from './Autocomplete.styles';
import { AutocompleteProps } from './AutocompleteProps';

const AutocompletePaper = (props: ComponentProps<typeof Paper>) => (
  <Paper {...props} css={styles.paper} />
);

const Autocomplete: FC<AutocompleteProps> = ({
  name,
  label,
  options,
  value,
  onChange,
  onBlur,
  disabled,
  fullWidth,
  error,
  helperText,
  css: cssProp,
  className,
}) => {
  const selectedOption =
    options.find((opt) => String(opt.value) === String(value ?? '')) ?? null;

  const handleChange = (
    _event: SyntheticEvent,
    newValue: Selectable | null
  ) => {
    onChange?.({ target: { value: newValue?.value ?? '' } });
  };

  return (
    <MuiAutocomplete
      css={styles.root}
      disabled={disabled}
      options={options}
      getOptionLabel={(option) => {
        const optionLabel = (option as Selectable).label;
        return typeof optionLabel === 'string' ? optionLabel : '';
      }}
      isOptionEqualToValue={(option, val) =>
        String((option as Selectable).value) ===
        String((val as Selectable).value)
      }
      value={selectedOption}
      onChange={handleChange}
      fullWidth={fullWidth}
      popupIcon={<Icons.ChevronDown width="20" height="20" />}
      slots={{ paper: AutocompletePaper }}
      renderInput={(params) => {
        const { InputLabelProps, InputProps, ...restParams } = params;
        const mergedOnBlur = (
          e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
          (InputProps as any).onBlur?.(e);
          onBlur?.(e);
        };
        return (
          <TextFields.Outlined
            {...restParams}
            name={name}
            label={label}
            css={cssProp}
            className={className}
            fullWidth={fullWidth}
            error={error}
            helperText={helperText}
            slotProps={{
              input: { ...InputProps, onBlur: mergedOnBlur },
              inputLabel: InputLabelProps,
            }}
          />
        );
      }}
    />
  );
};

export default Autocomplete;
